import { useState } from "react";
import "./OrderTrendsChart.scss";

export interface OrderTrendDatum {
  label: string;
  orders: number;
  gmvCents: number;
}

const BAR_COLOR = "#f4866a"; // 订单柱（左轴）
const LINE_COLOR = "#61a5ca"; // GMV 折线（右轴）
const TICKS = 4;

function niceCeil(v: number): number {
  if (v <= 0) return 1;
  const exp = Math.floor(Math.log10(v));
  const base = Math.pow(10, exp);
  const n = v / base;
  const nice = n <= 1 ? 1 : n <= 2 ? 2 : n <= 5 ? 5 : 10;
  return nice * base;
}

/** 分 → 美元短格式：$450 / $1.2k */
function usdShort(cents: number): string {
  const d = cents / 100;
  if (d >= 1000) return `$${(d / 1000).toFixed(1)}k`;
  return `$${Math.round(d).toLocaleString("en-US")}`;
}

/**
 * 订单趋势组合图：柱状图=订单数（左轴），折线=GMV（右轴），叠加展示。
 * 柱子用 CSS，折线用内联 SVG（vector-effect 保持线宽不被 viewBox 拉伸）。
 */
const OrderTrendsChart: React.FC<{
  data: OrderTrendDatum[];
  height?: number;
  maxLabels?: number;
}> = ({ data, height = 240, maxLabels = 10 }) => {
  const [hoverIdx, setHoverIdx] = useState<number | null>(null);

  if (!data.length) {
    return <div className="cs-combo__empty">暂无数据</div>;
  }

  const ordersMax = niceCeil(Math.max(...data.map((d) => d.orders), 1));
  const gmvMax = niceCeil(Math.max(...data.map((d) => d.gmvCents), 1));
  const labelEvery = Math.max(1, Math.ceil(data.length / maxLabels));

  const leftTicks = Array.from({ length: TICKS + 1 }).map((_, i) =>
    Math.round((ordersMax * (TICKS - i)) / TICKS)
  );
  const rightTicks = Array.from({ length: TICKS + 1 }).map(
    (_, i) => (gmvMax * (TICKS - i)) / TICKS
  );

  // 折线点（百分比坐标；y=0 顶部）
  const points = data.map((d, i) => ({
    x: ((i + 0.5) / data.length) * 100,
    y: 100 - (d.gmvCents / gmvMax) * 100,
  }));
  const polyline = points.map((p) => `${p.x},${p.y}`).join(" ");

  const hovered = hoverIdx != null ? data[hoverIdx] : null;
  const tipLeft =
    hoverIdx != null
      ? Math.min(92, Math.max(8, ((hoverIdx + 0.5) / data.length) * 100))
      : 0;

  return (
    <div className="cs-combo">
      <div className="cs-combo__plot">
        {/* 左 Y 轴：订单数 */}
        <div className="cs-combo__yaxis cs-combo__yaxis--left" style={{ height }}>
          {leftTicks.map((t, i) => (
            <div key={i} className="cs-combo__ytick">
              {t.toLocaleString("en-US")}
            </div>
          ))}
        </div>

        {/* 绘图区 */}
        <div className="cs-combo__bars-wrap" style={{ height }}>
          <div className="cs-combo__grid">
            {Array.from({ length: TICKS + 1 }).map((_, i) => (
              <div key={i} className="cs-combo__grid-line" />
            ))}
          </div>

          {/* 订单柱 */}
          <div className="cs-combo__bars">
            {data.map((d, i) => (
              <div
                key={`${d.label}-${i}`}
                className={`cs-combo__col${
                  hoverIdx === i ? " cs-combo__col--active" : ""
                }`}
                onMouseEnter={() => setHoverIdx(i)}
                onMouseLeave={() => setHoverIdx(null)}
              >
                <div
                  className="cs-combo__bar"
                  style={{
                    height: `${(d.orders / ordersMax) * 100}%`,
                    background: BAR_COLOR,
                  }}
                />
              </div>
            ))}
          </div>

          {/* GMV 折线 */}
          <svg
            className="cs-combo__line"
            viewBox="0 0 100 100"
            preserveAspectRatio="none"
          >
            <polyline
              points={polyline}
              fill="none"
              stroke={LINE_COLOR}
              strokeWidth={2}
              strokeLinejoin="round"
              strokeLinecap="round"
              vectorEffect="non-scaling-stroke"
            />
          </svg>
          {/* 折线数据点 */}
          {points.map((p, i) => (
            <span
              key={`dot-${i}`}
              className="cs-combo__dot"
              style={{
                left: `${p.x}%`,
                top: `${p.y}%`,
                background: LINE_COLOR,
              }}
            />
          ))}

          {/* Hover 提示 */}
          {hovered && (
            <div className="cs-combo__tip" style={{ left: `${tipLeft}%` }}>
              <div className="cs-combo__tip-date">{hovered.label}</div>
              <div className="cs-combo__tip-row">
                <span
                  className="cs-combo__tip-dot"
                  style={{ background: BAR_COLOR }}
                />
                <span className="cs-combo__tip-label">Orders</span>
                <span className="cs-combo__tip-val">
                  {hovered.orders.toLocaleString("en-US")}
                </span>
              </div>
              <div className="cs-combo__tip-row">
                <span
                  className="cs-combo__tip-dot"
                  style={{ background: LINE_COLOR }}
                />
                <span className="cs-combo__tip-label">GMV</span>
                <span className="cs-combo__tip-val">
                  {usdShort(hovered.gmvCents)}
                </span>
              </div>
            </div>
          )}
        </div>

        {/* 右 Y 轴：GMV */}
        <div
          className="cs-combo__yaxis cs-combo__yaxis--right"
          style={{ height }}
        >
          {rightTicks.map((t, i) => (
            <div key={i} className="cs-combo__ytick">
              {usdShort(t)}
            </div>
          ))}
        </div>
      </div>

      {/* X 轴日期 */}
      <div className="cs-combo__labels">
        {data.map((d, i) => (
          <div key={`label-${d.label}-${i}`} className="cs-combo__label">
            {i % labelEvery === 0 ? d.label : ""}
          </div>
        ))}
      </div>

      {/* 图例 */}
      <div className="cs-combo__legend">
        <div className="cs-combo__legend-item">
          <span className="cs-combo__swatch" style={{ background: BAR_COLOR }} />
          Orders
        </div>
        <div className="cs-combo__legend-item">
          <span
            className="cs-combo__swatch cs-combo__swatch--line"
            style={{ background: LINE_COLOR }}
          />
          GMV
        </div>
      </div>
    </div>
  );
};

export default OrderTrendsChart;
